import type { UserOutput } from "@orsane/schema/users";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAddTaskListUserMutation } from "@/mutations/task-list-mutations";
import { useMembers } from "@/queries/user-queries";
import { UserAvatar } from "./UserAvatar";

function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
    return (node: T) => {
        refs.forEach((ref) => {
            if (typeof ref === "function") {
                ref(node);
            } else if (ref != null) {
                (ref as React.MutableRefObject<T>).current = node;
            }
        });
    };
}

export function InvitePopover({
    selectedUsers,
    taskListId,
    ref,
}: {
    selectedUsers: UserOutput[];
    taskListId: number;
    ref: React.Ref<HTMLInputElement>;
}) {
    const { mutate: addUser } = useAddTaskListUserMutation();
    const [inputValue, setInputValue] = useState("");
    const { data: users } = useMembers({ staleTime: 0, gcTime: 0 }); // make sure to reload the data. you probably just added a user, so it needs to refresh
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const commandRef = useRef<HTMLDivElement>(null);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setIsOpen(value.length > 0);
    };

    const handleInputFocus = () => {
        if (inputValue.length > 0) {
            setIsOpen(true);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) return;

        // Forward navigation keys to the Command component
        if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();

            // Create and dispatch a new keyboard event to the Command component
            const commandElement = commandRef.current;
            if (commandElement) {
                const newEvent = new KeyboardEvent("keydown", {
                    key: e.key,
                    code: e.code,
                    ctrlKey: e.ctrlKey,
                    metaKey: e.metaKey,
                    shiftKey: e.shiftKey,
                    altKey: e.altKey,
                    bubbles: true,
                    cancelable: true,
                });
                commandElement.dispatchEvent(newEvent);
            }

            // Handle Escape to close popover
            if (e.key === "Escape") {
                setIsOpen(false);
            }
        }
    };

    const filteredUsers = useMemo(
        () =>
            (users || []).filter(
                (user) => [user.name, user.email].some((k) => k.includes(inputValue)) && !selectedUsers.some((u) => u.id === user.id)
            ),
        [users, inputValue, selectedUsers]
    );

    return (
        <div className="w-full">
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger
                    nativeButton={false}
                    render={
                        <div className="relative">
                            <input
                                ref={mergeRefs(inputRef, ref)}
                                type="text"
                                placeholder="Type to search..."
                                value={inputValue}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onKeyDown={handleKeyDown}
                                className="w-full h-full py-3 px-2 text-orsane-primary placeholder:text-orsane-placeholder outline-none"
                            />
                        </div>
                    }
                ></PopoverTrigger>
                <PopoverContent
                    className="p-0"
                    align="start"
                    side="bottom"
                    sideOffset={4}
                    style={{ width: inputRef.current?.offsetWidth }}
                    initialFocus={false}
                >
                    <Command ref={commandRef} shouldFilter={false}>
                        <CommandEmpty>No users found.</CommandEmpty>
                        <CommandList>
                            <CommandGroup>
                                {filteredUsers.map((user) => (
                                    <CommandItem
                                        value={user.id.toString()}
                                        key={user.id}
                                        keywords={[user.name, user.email]}
                                        onSelect={() => {
                                            setInputValue("");
                                            addUser({
                                                id: taskListId,
                                                userId: user.id,
                                            });
                                            setIsOpen(false);
                                            inputRef.current?.focus();
                                        }}
                                    >
                                        <UserAvatar user={user} size="sm" />
                                        {user.name || user.email}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
}
